import { useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import { useAuth } from './useAuth'
import { useWorkspace } from './useWorkspace'
import {
  CreditsContext,
  EMPTY_WORKSPACE_WALLET,
  type CreditsContextValue,
  type WorkspaceWalletBuckets,
} from './creditsContextCore'
import { isPersonalWorkspace } from '../utils/workspaceKind'
import { isHelperOnlyAccount } from '../utils/helperMembership'
import { getWrapperRole } from '../utils/wrapperRole'

const asCredits = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0

export const CreditsProvider = ({ children }: PropsWithChildren) => {
  const { currentUser, userProfile } = useAuth()
  const { workspaces, activeWorkspaceId } = useWorkspace()

  const uid = currentUser?.uid ?? null
  const activeWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? null,
    [workspaces, activeWorkspaceId],
  )
  const isActiveWorkspacePersonal = isPersonalWorkspace(activeWorkspace)
  const isHelper = isHelperOnlyAccount({ userProfile, currentUser, workspaces }) || getWrapperRole() === 'helper'

  const [subscribedPersonalCredits, setSubscribedPersonalCredits] = useState<{ uid: string; credits: number } | null>(null)
  const personalCredits = useMemo(() => {
    if (!uid || isHelper) return 0
    if (subscribedPersonalCredits?.uid === uid) return subscribedPersonalCredits.credits
    return 0
  }, [uid, isHelper, subscribedPersonalCredits])
  const personalCreditsLoading = useMemo(() => {
    if (!uid || isHelper) return false
    return subscribedPersonalCredits?.uid !== uid
  }, [uid, isHelper, subscribedPersonalCredits])

  const [subscribedWorkspaceWallet, setSubscribedWorkspaceWallet] = useState<{ workspaceId: string; wallet: WorkspaceWalletBuckets } | null>(null)
  const workspaceWallet = useMemo<WorkspaceWalletBuckets>(() => {
    if (!activeWorkspaceId || isHelper) return EMPTY_WORKSPACE_WALLET
    if (subscribedWorkspaceWallet?.workspaceId === activeWorkspaceId) {
      return subscribedWorkspaceWallet.wallet
    }
    return EMPTY_WORKSPACE_WALLET
  }, [activeWorkspaceId, isHelper, subscribedWorkspaceWallet])
  const workspaceWalletLoading = useMemo(() => {
    if (!activeWorkspaceId || isHelper) return false
    return subscribedWorkspaceWallet?.workspaceId !== activeWorkspaceId
  }, [activeWorkspaceId, isHelper, subscribedWorkspaceWallet])

  useEffect(() => {
    if (!uid || isHelper) {
      return undefined
    }
    const currentUid = uid
    return onSnapshot(
      doc(firestoreDb, 'users', currentUid, 'wallet', 'personal'),
      (snapshot) => {
        setSubscribedPersonalCredits({
          uid: currentUid,
          credits: asCredits(snapshot.data()?.availableCredits),
        })
      },
      (error) => {
        console.warn('[Credits] personal wallet listener failed', error)
        setSubscribedPersonalCredits({
          uid: currentUid,
          credits: 0,
        })
      },
    )
  }, [isHelper, uid])

  useEffect(() => {
    if (!activeWorkspaceId || isHelper) {
      return undefined
    }
    const currentWorkspaceId = activeWorkspaceId
    return onSnapshot(
      doc(firestoreDb, 'workspace_wallet', currentWorkspaceId),
      (snapshot) => {
        const data = snapshot.data()
        if (!snapshot.exists() || !data) {
          const fallbackAvailable = asCredits(activeWorkspace?.teamBalanceCredits ?? activeWorkspace?.credits)
          setSubscribedWorkspaceWallet({
            workspaceId: currentWorkspaceId,
            wallet: {
              ...EMPTY_WORKSPACE_WALLET,
              earnedCredits: fallbackAvailable,
              availableCredits: fallbackAvailable,
            },
          })
          return
        }
        const trialCredits = asCredits(data.trialCredits)
        const earnedCredits = asCredits(data.earnedCredits ?? data.availableCredits)
        setSubscribedWorkspaceWallet({
          workspaceId: currentWorkspaceId,
          wallet: {
            trialCredits,
            earnedCredits,
            availableCredits: asCredits(data.availableCredits ?? trialCredits + earnedCredits),
            reservedCredits: asCredits(data.reservedCredits),
            lifetimeSpentCredits: asCredits(data.lifetimeSpentCredits),
          },
        })
      },
      (error) => {
        console.warn('[Credits] workspace wallet listener failed', error)
        setSubscribedWorkspaceWallet({
          workspaceId: currentWorkspaceId,
          wallet: EMPTY_WORKSPACE_WALLET,
        })
      },
    )
  }, [activeWorkspace?.credits, activeWorkspace?.teamBalanceCredits, activeWorkspaceId, isHelper])

  const value = useMemo<CreditsContextValue>(() => {
    const contextualBalance = isActiveWorkspacePersonal ? personalCredits : workspaceWallet.availableCredits
    return {
      uid,
      activeWorkspaceId: activeWorkspaceId || null,
      isPersonalWorkspace: isActiveWorkspacePersonal,
      personalCredits,
      personalCreditsLoading,
      workspaceWallet,
      workspaceWalletLoading,
      contextualBalance,
      contextualScope: isActiveWorkspacePersonal ? 'personal' : 'workspace',
      spendableTotal: personalCredits + workspaceWallet.availableCredits,
    }
  }, [
    uid,
    activeWorkspaceId,
    isActiveWorkspacePersonal,
    personalCredits,
    personalCreditsLoading,
    workspaceWallet,
    workspaceWalletLoading,
  ])

  return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>
}
